/** Narrow shapes for POS `Order` / line items — kitchen completion display rules. */
export interface OrderItemKitchenLike {
  completed?: boolean
  cancelledAt?: string
}

export interface OrderKitchenLike {
  id: string
  status: string
  items: OrderItemKitchenLike[]
}

/** Line item still in progress for kitchen checklist (not cancelled, not completed). */
export function orderActiveItemPending(item: OrderItemKitchenLike): boolean {
  if (item.cancelledAt) return false
  return item.completed !== true
}

export function orderPendingItems(order: OrderKitchenLike): OrderItemKitchenLike[] {
  return order.items.filter((i) => orderActiveItemPending(i))
}

/** Any active line still not completed (order still has kitchen work). */
export function orderHasPendingKitchenItems(order: OrderKitchenLike): boolean {
  if (order.status === 'cancelled') return false
  return orderPendingItems(order).length > 0
}

/** All active lines are still incomplete (whole-order “all pending” yellow chrome). */
export function orderAllActiveItemsPending(order: OrderKitchenLike): boolean {
  if (order.status === 'cancelled') return false
  const active = order.items.filter((i) => !i.cancelledAt)
  if (active.length === 0) return false
  return active.every((i) => i.completed !== true)
}

/** Line item row: show “kitchen done” styling (green). Cancelled lines are excluded. */
export function orderItemRowKitchenDone(item: OrderItemKitchenLike): boolean {
  if (item.cancelledAt) return false
  return item.completed === true
}

/** Active (non-cancelled) lines must all be `completed` for checklist-complete. */
export function orderAllActiveLineItemsCompleted(order: OrderKitchenLike): boolean {
  if (order.status === 'cancelled') return false
  const active = order.items.filter((i) => !i.cancelledAt)
  if (active.length === 0) return true
  return active.every((i) => i.completed === true)
}

/** Order block: green when finished workflow or all active items completed on the checklist. */
export function orderShowsKitchenDone(order: OrderKitchenLike): boolean {
  if (order.status === 'cancelled') return false
  if (order.status === 'finished') return true
  return orderAllActiveLineItemsCompleted(order)
}

export interface BillKitchenLike {
  orderIds: string[]
}

/** True when every bill order id resolves and satisfies `orderShowsKitchenDone`. */
export function billOrdersAllKitchenDone(
  bill: BillKitchenLike,
  orders: readonly OrderKitchenLike[],
): boolean {
  if (bill.orderIds.length === 0) return false
  const map = new Map(orders.map((o) => [o.id, o]))
  for (const id of bill.orderIds) {
    const o = map.get(id)
    if (!o || !orderShowsKitchenDone(o)) return false
  }
  return true
}

/** Same as `billOrdersAllKitchenDone` but only for orders that belong to this table (`tableOrderIds`). */
export function billTableLinkedOrdersAllKitchenDone(
  bill: BillKitchenLike,
  tableOrderIds: ReadonlySet<string>,
  orders: readonly OrderKitchenLike[],
): boolean {
  const relevant = bill.orderIds.filter((id) => tableOrderIds.has(id))
  if (relevant.length === 0) return false
  const map = new Map(orders.map((o) => [o.id, o]))
  for (const id of relevant) {
    const o = map.get(id)
    if (!o || !orderShowsKitchenDone(o)) return false
  }
  return true
}

/** Table orders on this bill: every such order has all active lines still pending (yellow bill chrome). */
export function billAllTableOrdersFullyPending<T extends OrderKitchenLike & { billId?: string }>(
  billId: string,
  tableOrders: readonly T[],
): boolean {
  const rows = tableOrders.filter((o) => o.billId === billId)
  if (rows.length === 0) return false
  return rows.every((o) => orderAllActiveItemsPending(o))
}
